import usePageTitle from '../../hooks/usePageTitle';
import React, { useCallback, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ClipboardList,
  Play,
  CheckCircle,
  Clock,
  XCircle,
  Building2,
  ChevronRight,
} from 'lucide-react';
import { Inspection, Facility } from '../../types';
import { inspectionsService, facilitiesService } from '../../services';
import { useAuth } from '../../contexts/AuthContext';
import { usePortalScope } from '../../hooks/usePortalScope';
import { formatShortDate } from '../../utils/dateUtils';
import './ClientPortalInspections.css';

const STATUS_CONFIG = {
  not_started: { label: 'Requested', icon: Clock, className: 'status-not-started' },
  in_progress: { label: 'In Progress', icon: Play, className: 'status-in-progress' },
  complete: { label: 'Complete', icon: CheckCircle, className: 'status-complete' },
  cancelled: { label: 'Cancelled', icon: XCircle, className: 'status-cancelled' },
} as const;

const ClientPortalInspections: React.FC = () => {
  usePageTitle('Inspections');
  const { loading: authLoading } = useAuth();
  const { clientId, managerId, scopeFilter } = usePortalScope();
  const navigate = useNavigate();
  const [inspections, setInspections] = useState<Inspection[]>([]);
  const [facilities, setFacilities] = useState<Facility[]>([]);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    if (!scopeFilter) {
      setInspections([]);
      setFacilities([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const [inspectionsData, facilitiesData] = await Promise.all([
        inspectionsService.getInspections(scopeFilter),
        facilitiesService.getFacilities(scopeFilter),
      ]);

      setInspections(inspectionsData);
      setFacilities(facilitiesData);
    } catch (err) {
      console.error('Error loading inspections:', err);
    } finally {
      setLoading(false);
    }
  }, [clientId, managerId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!authLoading) {
      loadData();
    }
  }, [authLoading, loadData]);

  const getFacilityName = (facilityId: string) =>
    facilities.find((f) => f.id === facilityId)?.name ?? 'Unknown Facility';

  const requested = inspections
    .filter((i) => i.status === 'not_started')
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

  const history = inspections
    .filter((i) => i.status !== 'not_started')
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

  if (loading) {
    return (
      <div className="portal-inspections-loading">
        <div className="loading-spinner" />
        <p>Loading inspections...</p>
      </div>
    );
  }

  if (!clientId && !managerId) {
    return (
      <div className="portal-inspections-loading">
        <p>No account scope found. Please contact your administrator.</p>
      </div>
    );
  }

  return (
    <div className="portal-inspections">
      <div className="page-header">
        <div className="header-content">
          <h1>
            <ClipboardList size={28} />
            Inspections
          </h1>
          <p>View requested and completed inspections for your facilities</p>
        </div>
      </div>

      {/* Requested / Actionable */}
      <section className="inspections-section">
        <h2 className="section-title">
          <Clock size={18} />
          Requested
          {requested.length > 0 && (
            <span className="section-badge">{requested.length}</span>
          )}
        </h2>

        {requested.length === 0 ? (
          <div className="empty-state">
            <CheckCircle size={40} />
            <p>No pending inspections.</p>
          </div>
        ) : (
          <div className="inspection-cards">
            {requested.map((inspection) => (
              <div key={inspection.id} className="inspection-card inspection-card--requested">
                <div className="inspection-card-header">
                  <div className="facility-name">
                    <Building2 size={16} />
                    {getFacilityName(inspection.facility_id)}
                  </div>
                  <span className="status-badge status-not-started">Requested</span>
                </div>

                <div className="inspection-card-body">
                  <div className="inspection-meta">
                    <span className="meta-label">Requested:</span>
                    <span>{formatShortDate(inspection.created_at)}</span>
                  </div>
                  {inspection.summary && (
                    <p className="inspection-summary">{inspection.summary}</p>
                  )}
                </div>

                <div className="inspection-card-footer">
                  <button
                    className="btn btn-primary start-inspection-btn"
                    onClick={() =>
                      navigate(`/facilities/${inspection.facility_id}/inspection`)
                    }
                  >
                    <Play size={16} />
                    Start Inspection
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* History */}
      <section className="inspections-section">
        <h2 className="section-title">
          <ClipboardList size={18} />
          History
        </h2>

        {history.length === 0 ? (
          <div className="empty-state">
            <ClipboardList size={40} />
            <p>No completed inspections yet.</p>
          </div>
        ) : (
          <div className="inspection-cards">
            {history.map((inspection) => {
              const config = STATUS_CONFIG[inspection.status] ?? STATUS_CONFIG.in_progress;
              const StatusIcon = config.icon;

              return (
                <div
                  key={inspection.id}
                  className="inspection-card inspection-card--history"
                  onClick={() => navigate(`/inspections/${inspection.id}`)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && navigate(`/inspections/${inspection.id}`)}
                >
                  <div className="inspection-card-header">
                    <div className="facility-name">
                      <Building2 size={16} />
                      {getFacilityName(inspection.facility_id)}
                    </div>
                    <span className={`status-badge ${config.className}`}>
                      <StatusIcon size={12} />
                      {config.label}
                    </span>
                  </div>

                  <div className="inspection-card-body">
                    <div className="inspection-meta">
                      <span className="meta-label">Date:</span>
                      <span>{formatShortDate(inspection.created_at)}</span>
                    </div>
                    {inspection.summary && (
                      <p className="inspection-summary">{inspection.summary}</p>
                    )}
                  </div>

                  <div className="inspection-card-footer">
                    <span className="view-link">
                      View details <ChevronRight size={14} />
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
};

export default ClientPortalInspections;
